import { faFolder } from "@fortawesome/pro-solid-svg-icons"
import {
  Box,
  Checkbox,
  FormControlLabel,
  Grid,
  ListItemText,
  Switch,
  TextField,
  Typography,
  useTheme
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import Button from "components/Button"
import { CircleIcon } from "components/icons/CircleIcon"
import { RouterPrompt } from "components/RouterPrompt"
import { Formik } from "formik"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { useContext, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { IFeatureRoles, IRole, IRoleTemplateForm } from "./roleTemplates.interface"
import NonFormikSidebar from "components/sidedrawer/NonFormikSidebar"
import { StyledListItemHeaderGrid, StyledStickyButtonBox } from "components/sidedrawer/Sidebar.styled"
import { StyledListItem } from "shared/styled/General"
import * as Yup from "yup";
import { t } from "i18next";

const roleTemplateValidation = Yup.object().shape({
  name: Yup.string().required(t("global.name_is_required", "Name is required"))
})

const RoleTemplateForm = (props: IRoleTemplateForm) => {
  const { values, handleSubmit } = props
  const [userRolesResult, userRolesApi] = useApi() as [IApiResult, IApiFunc]
  const appContext = useContext(AppContext)
  const { t } = useTranslation()
  const theme = useTheme()

  useEffect(() => {
    userRolesApi.get(apiEndpoints.customeradmin.getuserroles)
  }, [])

  return (
    <NonFormikSidebar
      sidebarId="RoleTemplateForm"
    >
      <Formik
        onSubmit={handleSubmit}
        initialValues={values}
        enableReinitialize={true}
        validationSchema={roleTemplateValidation}
      >
        {({ values, handleChange, handleBlur, handleSubmit, setFieldValue, touched, errors }) => (
          <form id="RoleTemplateForm" onSubmit={handleSubmit} noValidate>
            <RouterPrompt />
            <Grid container pb={5} mt={0} pt={12}>
              <StyledListItemHeaderGrid flexDirection="row" xs={12} md={12} pb={3}>
                <CircleIcon
                  icon={faFolder}
                  backgroundColor={(theme.palette as any).primary[300]}
                  color={(theme.palette as any).primary[900]}
                  size="lg"
                  sx={{ marginRight: "14px" }}
                />
                <ListItemText
                  primary={
                    <Typography color="textPrimary" variant="h2">
                      {values.id > 0
                        ? t("settings.form.update_roletemplate", "Update role template")
                        : t("settings.form.button.create_roletemplate", "Create role template")}
                    </Typography>
                  }

                />
              </StyledListItemHeaderGrid>
            </Grid>
            <Box display="flex" flexDirection="column" gap={2} sx={{ width: "100%" }}>
              <Typography variant="labelMedium" fontWeight={500}>
                {t("settings.form.roletemplate.roletemplate", "Role template")}
              </Typography>
              <Grid item xs={12}>
                <TextField
                  sx={{ width: "100%" }}
                  autoComplete="off"
                  fullWidth
                  id="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  placeholder={t("settings.form.roletemplate.insert", "Insert name of role template")}
                  error={touched.name && errors.name ? true : false}
                  helperText={
                    touched.name && errors.name ? errors.name : null
                  }
                />

              </Grid>
              <StyledListItem>
                <Grid item md={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        id="global"
                        onChange={(e) => {
                          setFieldValue("global", e.target.checked)
                        }}
                        checked={values.global}
                        disabled={!hasRole(appContext.appUser, allRoles.SystemAdminCustomers)}
                      />
                    }
                    label={t("settings.form.global", "Global")}
                  />
                </Grid>
              </StyledListItem>

              <StyledListItem>
                <Grid item md={12}>
                  <Typography variant="h4" fontWeight={600}>
                    {t("settings.form.roles", "Roller") + ":"}
                  </Typography>
                  {userRolesResult.status === 2 &&
                    userRolesResult.data.map((value: IFeatureRoles) => {
                      return (
                        <Box mt={2}>
                          <Typography variant="labelMedium" fontWeight={500}>
                            {t("settings.user.modules." + value.translateId, value.translateId)}:
                          </Typography>
                          <Box sx={{ p: 0 }} display="flex" flexDirection="column" >
                            {value.roles.map((role: IRole) => {
                              return (
                                <StyledListItem>
                                  <FormControlLabel
                                    control={
                                      <Checkbox
                                        onChange={(_, checked) => {
                                          if (checked) {
                                            setFieldValue("roles", [...values.roles, role])
                                          } else {
                                            setFieldValue(
                                              "roles",
                                              values.roles.filter((x: IRole) => x.role !== role.role)
                                            )
                                          }
                                        }}
                                        id="role.role"
                                        value={role.role}
                                        checked={values.roles.map((x: IRole) => x.role).includes(role.role)}
                                      />
                                    }
                                    label={t("settings.user.modules." + role.translateId, role.translateId)}
                                  />
                                </StyledListItem>
                              )
                            })}
                          </Box>
                        </Box>
                      )
                    })}
                </Grid>
              </StyledListItem>
              <StyledStickyButtonBox p={{ xs: 3, sm: 4 }}>
                <Button type="submit">
                  {values.id > 0
                    ? t("global.buttons.save", "Save")
                    : t("settings.form.button.create_roletemplate", "Create role template")}
                </Button>
              </StyledStickyButtonBox>

            </Box>
          </form>
        )}
      </Formik>
    </NonFormikSidebar>
  )
}

export default RoleTemplateForm
